import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Building2, Users, Wrench, ShieldCheck, Boxes, ArrowLeft, Download, FileSpreadsheet, BarChart3 } from "lucide-react";
import * as XLSX from "xlsx";

const KIND_CFG = {
  distributors: { icon: Building2, label: "Distributors", color: "#E63946" },
  dealers: { icon: Users, label: "Dealers", color: "#FCA311" },
  installations: { icon: Wrench, label: "Installations", color: "#10B981" },
  warranties: { icon: ShieldCheck, label: "Warranties", color: "#3B82F6" },
  inventory: { icon: Boxes, label: "Inventory", color: "#A855F7" },
};

export default function Reports() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [filterQ, setFilterQ] = useState("");
  const [perfData, setPerfData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then(r => setSummary(r.data));
    api.get("/reports/performance").then(r => setPerfData(r.data));
  }, []);

  const openDrill = async (kind) => {
    setDrill(kind);
    setDrillLoading(true);
    try {
      const r = await api.get(`/reports/drilldown?kind=${kind}`);
      setDrillData(r.data);
    } finally { setDrillLoading(false); }
  };

  const backToSummary = () => { setDrill(null); setDrillData([]); setFilterQ(""); };

  const exportExcel = () => {
    if (!drillData?.length) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(drillData.map(row => flatten(row)));
    XLSX.utils.book_append_sheet(wb, ws, drill);
    XLSX.writeFile(wb, `cpl_${drill}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const cfg = drill ? KIND_CFG[drill] : null;

  // Filtered rows
  const rows = filterQ
    ? drillData.filter(r => JSON.stringify(r).toLowerCase().includes(filterQ.toLowerCase()))
    : drillData;

  if (!drill) {
    // Summary view
    if (!summary) return <div className="text-sm text-[#475569]">Loading…</div>;
    const boxes = user?.role === "manufacturer"
      ? ["distributors", "dealers", "installations", "warranties", "inventory"]
      : ["dealers", "installations", "warranties", "inventory"];

    return (
      <div className="space-y-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Analytics</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Reports</h1>
          <p className="text-[#475569] text-sm mt-1">Click a metric to drill into detailed rows and export.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {boxes.map((k) => {
            const c = KIND_CFG[k];
            const Icon = c.icon;
            return (
              <button key={k} data-testid={`summary-${k}`} onClick={() => openDrill(k)}
                className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-5 text-left hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{c.label}</div>
                  <Icon size={16} style={{ color: c.color }} />
                </div>
                <div className="text-4xl font-medium" style={{ fontFamily: "'Outfit', sans-serif", color: c.color }}>{summary[k]}</div>
                <div className="text-xs text-[#475569] mt-2">Click to view →</div>
              </button>
            );
          })}
        </div>

        {perfData && (
          <div className="grid lg:grid-cols-2 gap-6">
            <PerfBlock title="Dealer Performance" rows={perfData.dealers} field="dealer_name" cityField="dealer_city" />
            {user?.role === "manufacturer" && perfData.distributors?.length > 0 && (
              <PerfBlock title="Distributor Performance" rows={perfData.distributors} field="distributor_name" cityField="distributor_city" />
            )}
          </div>
        )}
      </div>
    );
  }

  // Drilldown view
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <button onClick={backToSummary} data-testid="drill-back" className="text-xs text-[#475569] hover:text-slate-900 inline-flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back to summary
          </button>
          <div className="flex items-center gap-3">
            {cfg && <cfg.icon size={22} style={{ color: cfg.color }} />}
            <h1 className="text-3xl tracking-tighter font-medium">{cfg.label} — {drillData.length}</h1>
          </div>
        </div>
        <button data-testid="drill-export" onClick={exportExcel} disabled={!drillData.length}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20 rounded-md text-sm disabled:opacity-50">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      <input data-testid="drill-filter" value={filterQ} onChange={(e) => setFilterQ(e.target.value)}
        placeholder="Filter rows..."
        className="w-full max-w-md px-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]" />

      {drillLoading ? <div className="text-sm text-[#475569]">Loading…</div> :
        rows.length === 0 ? (
          <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">No rows.</div>
        ) : (
          <DrillTable rows={rows} kind={drill} />
        )}
    </div>
  );
}

function DrillTable({ rows, kind }) {
  const columns = getColumns(kind, rows[0]);
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-x-auto">
      <table className="w-full text-sm" data-testid="drill-table">
        <thead><tr className="border-b border-[#E5E7EB] text-left">
          {columns.map(c => <th key={c.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold whitespace-nowrap">{c.label}</th>)}
        </tr></thead>
        <tbody>{rows.slice(0, 300).map((r, i) => (
          <tr key={r.id || i} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
            {columns.map(c => <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${c.mono ? "font-mono text-xs" : ""}`}>{c.render ? c.render(r[c.key], r) : String(r[c.key] ?? "-").slice(0, 80)}</td>)}
          </tr>
        ))}</tbody>
      </table>
      <div className="px-4 py-2 text-[10px] text-[#64748B]">Showing first 300 rows • Export to Excel for full data</div>
    </div>
  );
}

function getColumns(kind, sample) {
  const fmt = (v) => v ? new Date(v).toLocaleDateString("en-IN") : "-";
  const map = {
    distributors: [
      { key: "name", label: "Name" },
      { key: "gst_no", label: "GST", mono: true },
      { key: "phone", label: "Phone", mono: true },
      { key: "address_city", label: "City" },
      { key: "approval_status", label: "Status" },
      { key: "created_at", label: "Created", render: fmt },
    ],
    dealers: [
      { key: "name", label: "Name" },
      { key: "gst_no", label: "GST", mono: true },
      { key: "phone", label: "Phone", mono: true },
      { key: "address_city", label: "City" },
      { key: "created_at", label: "Created", render: fmt },
    ],
    installations: [
      { key: "customer_name", label: "Customer" },
      { key: "customer_phone", label: "Phone", mono: true },
      { key: "vehicle_reg_no", label: "Vehicle", mono: true },
      { key: "product_name", label: "Product" },
      { key: "sqft_required", label: "SqFt" },
      { key: "installer_name", label: "Installer" },
      { key: "installed_at", label: "Installed", render: fmt },
    ],
    warranties: [
      { key: "warranty_number", label: "Warranty #", mono: true },
      { key: "customer_name", label: "Customer" },
      { key: "vehicle_reg_no", label: "Vehicle", mono: true },
      { key: "product_name", label: "Product" },
      { key: "status", label: "Status" },
      { key: "issued_at", label: "Issued", render: fmt },
      { key: "expires_at", label: "Expires", render: (v, r) => r.is_lifetime ? "Lifetime" : fmt(v) },
    ],
    inventory: [
      { key: "ppf_code", label: "PPF Code", mono: true },
      { key: "product_name", label: "Product" },
      { key: "state", label: "State" },
      { key: "current_owner_role", label: "Owner" },
      { key: "remaining_sqft", label: "Remaining SqFt" },
      { key: "roll_size_sqft", label: "Roll SqFt" },
    ],
  };
  return map[kind] || Object.keys(sample || {}).slice(0, 8).map(k => ({ key: k, label: k }));
}

function PerfBlock({ title, rows, field, cityField }) {
  const max = Math.max(...(rows || []).map(r => r.installations || 0), 1);
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={14} className="text-[#E63946]" />
        <div className="text-sm font-medium">{title}</div>
      </div>
      {(rows || []).length === 0 ? (
        <div className="text-center py-8 text-[#64748B] text-sm">No data yet.</div>
      ) : (
        <div className="space-y-3" data-testid="perf-bars">
          {rows.map((r, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-1">
                <span>
                  <span className="text-slate-900">{r[field]}</span>
                  {cityField && r[cityField] && <span className="text-[#64748B] ml-2">· {r[cityField]}</span>}
                </span>
                <span className="font-mono text-[#475569]">{r.installations} installs • {r.available_stock ?? "-"} stock</span>
              </div>
              <div className="h-2 bg-[#FFFFFF] rounded">
                <div className="h-full bg-[#E63946] rounded" style={{ width: `${(r.installations / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function flatten(o) {
  const out = {};
  Object.keys(o || {}).forEach(k => {
    const v = o[k];
    if (typeof v === "object" && v !== null && !Array.isArray(v)) out[k] = JSON.stringify(v);
    else if (Array.isArray(v)) out[k] = JSON.stringify(v);
    else out[k] = v;
  });
  return out;
}
